import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type EventRow = {
  id: string;
  slug: string | null;
  title: string;
  starts_at: string;
  ends_at: string | null;
  tag: string | null;
  notes: string | null;
  requires_approval: boolean;
  requires_deposit: boolean;
  deposit_amount_cents: number | null;
  published: boolean;
  banner_url: string | null;
  gallery_urls: string[];
  created_at: string;
};

const SELECT_COLS = "id,slug,title,starts_at,ends_at,tag,notes,requires_approval,requires_deposit,deposit_amount_cents,published,banner_url,gallery_urls,created_at";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Public: list upcoming published events (homepage / events page). */
export const listPublishedEvents = createServerFn({ method: "GET" })
  .inputValidator((data: { limit?: number } | undefined) => ({
    limit: data?.limit ?? 8,
  }))
  .handler(async ({ data }): Promise<EventRow[]> => {
    const supabase = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: rows, error } = await supabase
      .from("events")
      .select(SELECT_COLS)
      .eq("published", true)
      .gte("starts_at", new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString())
      .order("starts_at", { ascending: true })
      .limit(data.limit);
    if (error) return [];
    return (rows ?? []) as EventRow[];
  });

/** Public: fetch a single published event by slug (or legacy uuid). */
export const getPublishedEvent = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) =>
    z.object({ id: z.string().min(1).max(200) }).parse(data),
  )
  .handler(async ({ data }): Promise<EventRow | null> => {
    const supabase = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const key = data.id;
    const column = UUID_RE.test(key) ? "id" : "slug";
    const { data: row, error } = await supabase
      .from("events")
      .select(SELECT_COLS)
      .eq("published", true)
      .eq(column, key)
      .maybeSingle();
    if (error) return null;
    return (row ?? null) as EventRow | null;
  });

/** Staff: list all events (admin). */
export const listAllEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<EventRow[]> => {
    const { data, error } = await context.supabase
      .from("events")
      .select(SELECT_COLS)
      .order("starts_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []) as EventRow[];
  });

const urlOrPath = z
  .string()
  .trim()
  .max(2000)
  .refine((v) => /^https?:\/\//i.test(v) || v.startsWith("/api/public/media/"), {
    message: "Must be a URL or an uploaded media path.",
  });

const UpsertSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().trim().min(2).max(160),
  starts_at: z.string().min(8),
  ends_at: z.string().min(8).optional().nullable(),
  tag: z.string().trim().max(40).optional().nullable(),
  notes: z.string().trim().max(500).optional().nullable(),
  banner_url: urlOrPath.optional().nullable(),
  gallery_urls: z.array(urlOrPath).max(24).optional().default([]),
  published: z.boolean().default(true),
});


export const upsertEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => UpsertSchema.parse(data))
  .handler(async ({ data, context }) => {
    const payload: Record<string, unknown> = {
      title: data.title,
      starts_at: data.starts_at,
      ends_at: data.ends_at ?? null,
      tag: data.tag ?? null,
      notes: data.notes ?? null,
      banner_url: data.banner_url ?? null,
      gallery_urls: data.gallery_urls ?? [],
      published: data.published,
    };
    if (data.id) {
      const { error } = await context.supabase.from("events").update(payload as never).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase.from("events").insert(payload as never).select("id").single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });

export const deleteEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("events").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
